import { useAudioStore } from '../../stores/audioStore';
import { AmbientSynth } from './synthesizers/AmbientSynth';
import { CountdownSynth } from './synthesizers/CountdownSynth';
import { AlertSynth } from './synthesizers/AlertSynth';
import { EffectsSynth } from './synthesizers/EffectsSynth';

class AudioEngine {
  private static instance: AudioEngine | null = null;
  private audioContext: AudioContext | null = null;
  private masterGain: GainNode | null = null;
  private initialized = false;

  // Synthesizers
  private ambientSynth: AmbientSynth | null = null;
  private countdownSynth: CountdownSynth | null = null;
  private alertSynth: AlertSynth | null = null;
  private effectsSynth: EffectsSynth | null = null;

  private constructor() {}

  static getInstance(): AudioEngine {
    if (!AudioEngine.instance) {
      AudioEngine.instance = new AudioEngine();
    }
    return AudioEngine.instance;
  }

  async initialize(): Promise<void> {
    if (this.initialized) return;

    try {
      this.audioContext = new AudioContext();

      // Resume context if suspended (browser autoplay policy)
      if (this.audioContext.state === 'suspended') {
        await this.audioContext.resume();
      }

      // Create master gain
      this.masterGain = this.audioContext.createGain();
      this.masterGain.connect(this.audioContext.destination);

      // Initialize synths
      this.ambientSynth = new AmbientSynth(this.audioContext, this.masterGain);
      this.countdownSynth = new CountdownSynth(this.audioContext, this.masterGain);
      this.alertSynth = new AlertSynth(this.audioContext, this.masterGain);
      this.effectsSynth = new EffectsSynth(this.audioContext, this.masterGain);

      // Subscribe to audio store for volume/mute changes
      this.syncWithStore();

      this.initialized = true;
    } catch (error) {
      console.error('Failed to initialize audio engine:', error);
    }
  }

  private syncWithStore(): void {
    const { volume, isMuted } = useAudioStore.getState();
    this.setVolume(volume);
    this.setMuted(isMuted);

    // Subscribe to store changes
    useAudioStore.subscribe((state) => {
      this.setVolume(state.volume);
      this.setMuted(state.isMuted);
    });
  }

  setVolume(volume: number): void {
    if (this.masterGain && this.audioContext) {
      const { isMuted } = useAudioStore.getState();
      this.masterGain.gain.setValueAtTime(
        isMuted ? 0 : volume,
        this.audioContext.currentTime
      );
    }
  }

  setMuted(muted: boolean): void {
    if (this.masterGain && this.audioContext) {
      const { volume } = useAudioStore.getState();
      this.masterGain.gain.setValueAtTime(
        muted ? 0 : volume,
        this.audioContext.currentTime
      );
    }
  }

  isInitialized(): boolean {
    return this.initialized;
  }

  getContext(): AudioContext | null {
    return this.audioContext;
  }

  // Countdown sounds
  playCountdownBeep(count: number): void {
    if (!this.initialized || !this.countdownSynth) return;
    this.countdownSynth.playBeep(count);
  }

  playGoSound(): void {
    if (!this.initialized || !this.countdownSynth) return;
    this.countdownSynth.playGo();
  }

  // Ambient drone
  startAmbient(): void {
    if (!this.initialized || !this.ambientSynth) return;
    this.ambientSynth.start();
  }

  stopAmbient(): void {
    if (!this.initialized || !this.ambientSynth) return;
    this.ambientSynth.stop();
  }

  setAmbientIntensity(intensity: number): void {
    if (!this.initialized || !this.ambientSynth) return;
    this.ambientSynth.setIntensity(intensity);
  }

  // Alert sound
  startAlert(): void {
    if (!this.initialized || !this.alertSynth) return;
    this.alertSynth.start();
  }

  stopAlert(): void {
    if (!this.initialized || !this.alertSynth) return;
    this.alertSynth.stop();
  }

  // Effect sounds
  playStartSound(): void {
    if (!this.initialized || !this.effectsSynth) return;
    this.effectsSynth.playStart();
  }

  playSuccessSound(): void {
    if (!this.initialized || !this.effectsSynth) return;
    this.effectsSynth.playSuccess();
  }

  playFailureSound(): void {
    if (!this.initialized || !this.effectsSynth) return;
    this.effectsSynth.playFailure();
  }

  playPersonalBestSound(): void {
    if (!this.initialized || !this.effectsSynth) return;
    this.effectsSynth.playPersonalBest();
  }

  playHighScoreSound(): void {
    if (!this.initialized || !this.effectsSynth) return;
    this.effectsSynth.playHighScore();
  }

  playRank3Sound(): void {
    if (!this.initialized || !this.effectsSynth) return;
    this.effectsSynth.playRank3();
  }

  // Cleanup
  dispose(): void {
    this.ambientSynth?.stop();
    this.alertSynth?.stop();

    if (this.audioContext) {
      this.audioContext.close();
    }

    this.audioContext = null;
    this.masterGain = null;
    this.ambientSynth = null;
    this.countdownSynth = null;
    this.alertSynth = null;
    this.effectsSynth = null;
    this.initialized = false;
  }
}

export const audioEngine = AudioEngine.getInstance();
